import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookMarked, Bot, Brain, ChevronDown, ChevronRight, ChevronUp, FileCode, FilePlus, Folder, Loader2, Play, Plus, Square, Trash2, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import RunSteps from "../components/RunSteps";
import { outcomeSentence, stepsForRun, stepsFromAgent } from "../lib/run-steps";
import AgentSteps from "../components/AgentSteps";
import CodeVoorstel from "../components/CodeVoorstel";
import { crumbs, levelAt, readableSize } from "../lib/file-tree";
import { useTaal } from "@/contexts/TaalContext";
import CodeVoorbeelden from "../components/CodeVoorbeelden";
import CodeModelKeuze from "../components/CodeModelKeuze";
import ReposVenster from "../components/ReposVenster";
import RepoVastleggingen from "../components/RepoVastleggingen";
import CodeRepoBalk from "../components/CodeRepoBalk";
import ServerPaneel from "../components/ServerPaneel";
import DeelstappenPaneel from "../components/DeelstappenPaneel";
import { geleerdeRegel, werkruimteNaam } from "../lib/werkruimte-tekst";
import { useKaartToken } from "../lib/kaartToken";
import CodeTeamLabel from "@/components/CodeTeamLabel";

/**
 * The coding workspace.
 *
 * Two screens in one route, because on a phone they cannot be side by side:
 * the list of workspaces, and one workspace open. Opening one replaces the
 * list and the back arrow returns, the same shape the document viewer uses.
 *
 * The layout is built around **giving a task and watching it happen**, which
 * is where the agent loop lands next. The box at the bottom is the task box;
 * today it runs the open file, later it takes a sentence. Everything above it
 * is the step list, which grows kinds of step rather than changing shape.
 *
 * Refusals are never summarised. Phase 2b measured that pip prints its own
 * retry warnings over our 403, so this screen is the only place the sentence
 * can reach a person.
 */
/** FIX 5: wat je moet doen, en niet alleen wat er mis is. */
export const GEEN_BESTAND =
  "Er is nog geen bestand open. Kies een bestand in de lijst, of typ boven de editor een naam " +
  "(bijvoorbeeld hallo.py), zet er code in en klik dan op Dit bestand uitvoeren. " +
  "Wil je dat de agent het bestand maakt? Typ je opdracht onderaan en druk op Enter.";

/** GL223-2: "Installatie wacht op jou" in het verloop vervangen door de afloop. PUUR. */
export function naInstallatie(stappen, afloop) {
  let vervangen = false;
  const uit = stappen.map((s) => {
    if (s.kind === "ask" && s.title === "Installatie wacht op jou") {
      vervangen = true;
      return { ...s, ...afloop, kind: afloop.kind };
    }
    return s;
  });
  return vervangen ? uit : [...stappen, { ...afloop, id: `agent-${stappen.length}` }];
}

/** FIX 6: de stappen van de lopende rit uit het log van de werkruimte. */
export function stappenSinds(log, sinds) {
  return (Array.isArray(log) ? log : [])
    .filter((regel) => regel?.type === "agent_step" && (regel.at || 0) >= sinds);
}

/** FIX 6: hoe vaak het scherm het log leest terwijl de agent loopt. Lokaal, geen limiet. */
export const LOG_MEEKIJKEN_MS = 1500;

/** Glasronde 0.9.221 punt 4: wat het bewerkvak na Toepassen toont. PUUR. Ongewijzigd bewerkvak: de nieuwe inhoud.
 *  Niet-opgeslagen wijziging: die blijft staan, met een waarschuwing, want stil overschrijven is werk weggooien. */
export function naToepassen(huidig, geladen, nieuw) {
  if (huidig === geladen) return { inhoud: nieuw, geladen: nieuw, waarschuw: false };
  if (huidig === nieuw) return { inhoud: huidig, geladen: nieuw, waarschuw: false };
  return { inhoud: huidig, geladen: nieuw, waarschuw: true };
}

export default function CodePage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [open, setOpen] = useState(null);
  // CA8: "Laat de agent dit oplossen" in een issue opent de werkruimte van de repo en start daar de rit.
  const [beginOpdracht, setBeginOpdracht] = useState("");
  const [loading, setLoading] = useState(true);
  // EG1: het eigen Git-systeem opent als venster vanuit Code (App.js is bevroren).
  const [reposOpen, setReposOpen] = useState(false);

  const load = useCallback(
    () =>
      axios
        .get(`${API}/workspaces`)
        .then((r) => setWorkspaces(r.data))
        .catch(() => {})
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // CA5 (besluit Frank 1 nov): na Bevestig op de kaart "bouw een app" in de chat opent Code die werkruimte en start de
  // rit, EEN keer: de server zet het voorstel op gestart; lukt dat niet (al gestart), dan alleen de werkruimte openen.
  const [zoekParams] = useSearchParams();
  const appVoorstel = zoekParams.get("app");
  useEffect(() => {
    if (!appVoorstel) return;
    let levend = true;
    (async () => {
      let rij;
      let starten = true;
      try {
        rij = (await axios.post(`${API}/app-opdrachten/${encodeURIComponent(appVoorstel)}/gestart`)).data;
      } catch {
        starten = false;
        try {
          rij = (await axios.get(`${API}/app-opdrachten/${encodeURIComponent(appVoorstel)}`)).data;
        } catch {
          return;
        }
      }
      if (!levend || !rij?.workspace_id) return;
      const { data } = await axios.get(`${API}/workspaces/${rij.workspace_id}`);
      if (!levend) return;
      if (starten) setBeginOpdracht(rij.opdracht);
      setOpen(data);
    })().catch(() => {});
    return () => { levend = false; };
  }, [appVoorstel]);

  async function create() {
    try {
      const { data } = await axios.post(`${API}/workspaces`, { name: t("Naamloze werkruimte") });
      await load();
      setOpen(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("Er kon geen werkruimte worden gemaakt"));
    }
  }

  async function losIssueOp(repo, nummer) {
    setBeginOpdracht(`Los issue #${nummer} op`);
    await openRepo(repo);
  }

  // EGC: een repo kiezen opent zijn werkruimte, of maakt er een met de bestanden van main en een eigen tak.
  async function openRepo(repo) {
    const bestaand = workspaces.find((w) => w.repo?.id === repo.id);
    if (bestaand) {
      setReposOpen(false);
      setOpen(bestaand);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/workspaces`, { name: repo.name, repo_id: repo.id });
      await load();
      setReposOpen(false);
      setOpen(data);
    } catch (error) {
      toast.error(t(error.response?.data?.detail || "Er kon geen werkruimte worden gemaakt"));
    }
  }

  if (open) {
    return (
      <Workspace
        // Glasronde 0.9.230 punt 1: een andere werkruimte is een nieuwe Workspace. Zonder sleutel bleef `detail` (en
        // daarmee de netwerkkeuze in de kop) van de vorige staan als een open werkruimte wisselde via /code?app=.
        key={open.id}
        workspace={open}
        beginOpdracht={beginOpdracht}
        onBegonnen={() => setBeginOpdracht("")}
        onBack={() => {
          setOpen(null);
          load();
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-background ${BOTTOM_NAV_SPACER}`}>
      <header className="flex items-center gap-2 border-b px-3 pb-2 pt-[calc(0.5rem+var(--veilig-boven))]">
        <Button
          variant="ghost"
          size="icon"
          className="tap-target"
          onClick={() => navigate("/")}
          aria-label={t("Terug naar de chat")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-base font-semibold">{t("Code")}</h1>
        <Button size="sm" variant="outline" className="tap-target ml-auto" onClick={() => setReposOpen(true)}
                data-testid="open-repos">
          <BookMarked className="h-4 w-4 mr-1" />
          {t("Repo's")}
        </Button>
        <Button size="sm" className="tap-target" onClick={create} data-testid="new-workspace">
          <Plus className="h-4 w-4 mr-1" />
          {t("Nieuw")}
        </Button>
      </header>

      <div className="p-3 space-y-2">
        {loading && <p className="text-sm text-muted-foreground">{t("Laden…")}</p>}

        {!loading && workspaces.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <FileCode className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">{t("Nog geen werkruimtes")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("Een werkruimte is een map waarin code kan draaien zonder iets anders op deze machine te raken. Geen netwerk, tenzij jij het toestaat.")}
            </p>
            <Button className="tap-target mt-4" onClick={create}>
              <Plus className="h-4 w-4 mr-1" />
              {t("Maak er een")}
            </Button>
          </div>
        )}

        {workspaces.map((workspace) => (
          <button
            key={workspace.id}
            type="button"
            onClick={() => setOpen(workspace)}
            className="tap-target flex w-full items-center gap-3 rounded-lg border p-3 text-left hover:bg-muted/50"
            data-testid={`workspace-${workspace.id}`}
          >
            <FileCode className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium" data-testid="werkruimte-naam">{werkruimteNaam(workspace.name, t)}</span>
            <NetworkChip mode={workspace.network?.mode} />
          </button>
        ))}
      </div>
      <ReposVenster open={reposOpen} onOpenChange={setReposOpen} onKies={openRepo} onLosIssueOp={losIssueOp} />
      <MobileBottomNav />
    </div>
  );
}

// GL222-1 (glasronde 0.9.222): in een geopende werkruimte is het netwerk een KEUZE en geen label. CA2 (besluit Frank
// 31 okt): standaard uit, per werkruimte aan te zetten voor pakketten; elke installatie vraagt daarna nog toestemming, en
// toetsen draaien altijd zonder netwerk. Een stand met alleen toegestane adressen (uit een eerdere toestemming bij een
// run) blijft zichtbaar en is terug te zetten, maar wordt hier niet nieuw gekozen.
/** De foutmelding bij het omzetten van het netwerk, met de reden: de weigering van de backend, of dat er geen
 *  antwoord kwam (dan bereikte het verzoek de backend niet). PUUR. */
export function netwerkFout(error, t) {
  const tekst = error?.response?.data?.detail;
  if (typeof tekst === "string") return tekst;
  if (error?.response) return t("Dat kon niet worden gewijzigd (fout {code}).", { code: error.response.status });
  return t("Dat kon niet worden gewijzigd: de backend gaf geen antwoord ({reden}). Probeer het opnieuw.",
    { reden: error?.message || t("onbekend") });
}

export function NetworkKeuze({ detail, onGewijzigd }) {
  const { t } = useTaal();
  const mode = detail?.network?.mode || "off";
  const [bezig, setBezig] = useState(false);

  async function zet(nieuw) {
    if (nieuw === mode) return;
    setBezig(true);
    try {
      const { data } = await axios.put(`${API}/workspaces/${detail.id}/network`, { mode: nieuw, hosts: [] });
      onGewijzigd(data);
      toast.success(nieuw === "packages"
        ? t("Netwerk aan voor pakketten (PyPI en npm). Elke installatie vraagt nog jouw toestemming; toetsen draaien zonder netwerk.")
        : t("Het netwerk van deze werkruimte staat uit."));
    } catch (error) {
      // Glasronde 0.9.229 fout 7: de eerste poging bereikte de backend niet (geen verzoek in het log) en de melding
      // verdween voor hij te lezen was. Nu blijft hij staan tot je hem wegklikt, met de reden erbij.
      toast.error(netwerkFout(error, t), { duration: Infinity, closeButton: true });
    } finally {
      setBezig(false);
    }
  }

  return (
    <label className="ml-auto flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
           data-testid="network-keuze">
      {mode === "off" ? <WifiOff className="h-3 w-3" /> : <Wifi className="h-3 w-3 text-ok" />}
      <select value={mode} disabled={bezig} onChange={(event) => zet(event.target.value)}
              aria-label={t("Netwerk van deze werkruimte")} className="tap-target bg-transparent" data-testid="network-select">
        <option value="off">{t("Geen netwerk")}</option>
        <option value="packages">{t("Netwerk voor pakketten")}</option>
        {mode === "hosts" && <option value="hosts">{t("Alleen toegestane adressen")}</option>}
      </select>
    </label>
  );
}

/** Glasronde 0.9.230 punt 2: de stand van het netwerk in woorden van de app, nooit de kale modusnaam. PUUR. */
export function netwerkLabel(mode, t) {
  if (mode === "packages") return t("Netwerk voor pakketten");
  if (mode === "hosts") return t("Alleen toegestane adressen");
  return t("Geen netwerk");
}

function NetworkChip({ mode }) {
  const { t } = useTaal();
  const off = !mode || mode === "off";
  return (
    <span
      className="ml-auto flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground"
      data-testid="network-chip"
    >
      {off ? <WifiOff className="h-3 w-3" /> : <Wifi className="h-3 w-3 text-ok" />}
      {netwerkLabel(mode, t)}
    </span>
  );
}

/** Glasronde 0.9.230 (telefoon): of het onderste blok in Code ingeklapt staat, per apparaat onthouden. Opslag kan
 *  ontbreken (privévenster): dan gewoon uitgeklapt. */
export const BALK_SLEUTEL = "novamund_code_balk_ingeklapt";
function leesBalkIn() {
  try { return localStorage.getItem(BALK_SLEUTEL) === "1"; } catch { return false; }
}
function bewaarBalkIn(waarde) {
  try { localStorage.setItem(BALK_SLEUTEL, waarde ? "1" : "0"); } catch { /* geen opslag: alleen voor nu */ }
}

function Workspace({ workspace, onBack, beginOpdracht = "", onBegonnen = () => {} }) {
  const { t, taal } = useTaal();
  const [detail, setDetail] = useState(workspace);
  const [balkIn, setBalkIn] = useState(leesBalkIn);
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState("");
  const [content, setContent] = useState("");
  // Glasronde 0.9.221 punt 4: de inhoud zoals hij het laatst van schijf kwam of erheen ging. Verschilt het bewerkvak
  // daarvan, dan heeft de persoon iets getypt dat nog niet is opgeslagen.
  const [geladen, setGeladen] = useState("");
  // GL222-3: of het open bestand door Toepassen werd geopend (en niet door een klik van de persoon).
  const doorToepassen = useRef(false);
  const [installeren, setInstalleren] = useState(false);
  // B3-2: de installatiekaart draagt haar WF-40-token, per gevraagd plan een vers.
  const wachtend = detail?.installatie;
  const { token: installatieToken } = useKaartToken(
    wachtend ? `${API}/workspaces/${detail.id}/installatie/toon?plan=${wachtend.gevraagd_op || ""}` : "", Boolean(wachtend));
  const [steps, setSteps] = useState([]);
  const [running, setRunning] = useState(false);
  const [granting, setGranting] = useState(false);
  const [lastRequest, setLastRequest] = useState(null);
  const [folder, setFolder] = useState("");
  const [learned, setLearned] = useState([]);
  const [task, setTask] = useState("");
  const [agentSteps, setAgentSteps] = useState([]);
  const [outcome, setOutcome] = useState("");
  const [working, setWorking] = useState(false);
  // Glasronde 0.9.229 fout 6: welke rit om de installatie vroeg, zodat hij na Installeer vanzelf verder gaat.
  const laatsteRit = useRef(null);
  const [deelstapVerder, setDeelstapVerder] = useState(0);
  // Blok 3: na elke agentrit het voorstel opnieuw lezen.
  const [voorstelVersie, setVoorstelVersie] = useState(0);
  // EGC: na toepassen (Bevestig per bestand) de vastleggingen uit de repo opnieuw lezen.
  const [vastlegVersie, setVastlegVersie] = useState(0);

  const loadFiles = useCallback(() => {
    axios
      .get(`${API}/workspaces/${workspace.id}/files`)
      .then((r) => setFiles(r.data))
      .catch(() => {});
  }, [workspace.id]);

  const loadLearned = useCallback(() => {
    axios
      .get(`${API}/workspaces/${workspace.id}/memory`)
      .then((r) => setLearned(r.data))
      .catch(() => {});
  }, [workspace.id]);

  useEffect(() => {
    loadFiles();
    loadLearned();
  }, [loadFiles, loadLearned]);

  // GL223-4 (glasronde 0.9.223): na verversen het verloop van de laatste agentrit en de laatste uitvoer terug, per
  // werkruimte, zoals de backend ze bewaarde. Wat al in beeld staat (een rit die net liep) wordt niet overschreven.
  useEffect(() => {
    let weg = false;
    axios.get(`${API}/workspaces/${workspace.id}/laatste`).then(({ data }) => {
      if (weg || !data) return;
      if (data.agent?.stappen?.length) {
        setAgentSteps((nu) => (nu.length ? nu : stepsFromAgent(data.agent.stappen)));
        if (data.agent.outcome) {
          setOutcome((nu) => nu || outcomeSentence(data.agent.outcome, data.agent.rounds, data.agent.stappen, taal));
        }
      }
      if (data.uitvoer?.verzoek && data.uitvoer?.uitslag) {
        const verzoek = { ...data.uitvoer.verzoek, title: `${data.uitvoer.verzoek.path} uitgevoerd` };
        setLastRequest((nu) => nu || verzoek);
        setSteps((nu) => (nu.length ? nu : stepsForRun(verzoek, data.uitvoer.uitslag)));
      }
    }).catch(() => {});
    return () => { weg = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.id]);

  async function forget(id, text) {
    await axios.delete(`${API}/workspaces/${workspace.id}/memory/${id}`);
    loadLearned();
    toast.success(t("Vergeten: {tekst}", { tekst: text }));
  }

  async function openFile(name, viaToepassen = false) {
    const { data } = await axios.get(`${API}/workspaces/${workspace.id}/files/${name}`);
    doorToepassen.current = viaToepassen;
    setPath(name);
    setContent(data.content);
    setGeladen(data.content);
  }

  // Glasronde 0.9.221 punt 4: na Toepassen (of Terugdraaien) toont het bewerkvak de nieuwe inhoud van het open bestand.
  // Een niet-opgeslagen wijziging wordt niet stil overschreven; dan zegt een melding wat er is gebeurd.
  // GL222-3: staat er geen bestand open, dan opent Toepassen het toegepaste bestand; anders bleef het bewerkvak leeg, en
  // met de oude voorbeeldtekst print('hallo') leek dat op een main.py die niet was bijgewerkt.
  async function herlaadOpen(toegepast) {
    // Door Toepassen geopend en niet gewijzigd: het bewerkvak volgt het bestand dat nu is toegepast.
    if (toegepast && (!path || (doorToepassen.current && content === geladen && toegepast !== path))) {
      await openFile(toegepast, true);
      return;
    }
    if (!path) return;
    let data;
    try {
      ({ data } = await axios.get(`${API}/workspaces/${workspace.id}/files/${path}`));
    } catch {
      return;
    }
    const keuze = naToepassen(content, geladen, data.content);
    if (keuze.inhoud !== content) setContent(keuze.inhoud);
    setGeladen(keuze.geladen);
    if (keuze.waarschuw) toast.warning(t("{pad} is gewijzigd door Toepassen; jouw niet-opgeslagen versie staat nog in het bewerkvak.", { pad: path }));
  }

  // CA2 (besluit Frank 31 okt): per installatie toestemming, met de lijst van wat er meekomt.
  async function installatie(keuze) {
    setInstalleren(true);
    try {
      const { data } = keuze === "bevestig"
        ? await axios.post(`${API}/workspaces/${workspace.id}/installatie/bevestig`, { token: installatieToken })
        : await axios.post(`${API}/workspaces/${workspace.id}/installatie/weigeren`);
      // GL223-2: de wachtende stap maakt plaats voor de afloop, zoals de backend hem in het log schreef.
      if (data?.stap) setAgentSteps((s) => naInstallatie(s, data.stap));
      const verder = keuze === "bevestig" ? laatsteRit.current : null;
      toast.success(keuze !== "bevestig" ? t("Installatie geweigerd.")
        : verder ? t("Geïnstalleerd. De agent gaat verder.") : t("Geïnstalleerd. Start de agent opnieuw om het te gebruiken."));
      setDetail((d) => { const { installatie: _weg, ...rest } = d; return rest; });
      loadFiles();
      if (verder?.soort === "taak") doTask(verder.opdracht);
      else if (verder?.soort === "deelstap") setDeelstapVerder((n) => n + 1);
    } catch (error) {
      const tekst = error.response?.data?.detail;
      toast.error(typeof tekst === "string" ? tekst : t("De installatie lukte niet."));
    } finally {
      setInstalleren(false);
    }
  }

  async function zetRondes(waarde) {
    try {
      const { data } = await axios.put(`${API}/workspaces/${workspace.id}/rondes`, { max_rondes: Number(waarde) });
      setDetail(data);
    } catch {
      toast.error(t("Het aantal rondes kon niet worden opgeslagen."));
    }
  }

  async function save() {
    if (!path.trim()) return;
    await axios.put(`${API}/workspaces/${workspace.id}/files/${path}`, { content });
    setGeladen(content);
    loadFiles();
    toast.success(t("Opgeslagen"));
  }

  async function run() {
    if (!path.trim()) {
      toast.error(GEEN_BESTAND);
      return;
    }
    setRunning(true);
    const language = path.endsWith(".sh") ? "bash" : "python";
    // GL223-1 (glasronde 0.9.223): de titel in het Nederlands, zoals elke stap; AgentSteps vertaalt bij het tonen.
    const request = { path, language, title: `${path} uitgevoerd` };
    try {
      await axios.put(`${API}/workspaces/${workspace.id}/files/${path}`, { content });
      const { data } = await axios.post(`${API}/workspaces/${workspace.id}/run`, {
        path,
        language,
      });
      setLastRequest(request);
      setSteps(stepsForRun(request, data));
      loadFiles();
      loadLearned();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      // Every refusal the backend can give, said in words rather than swallowed.
      toast.error(
        status === 402
          ? t("Code uitvoeren vraagt het Pro-abonnement")
          : status === 501
            ? detail || t("Deze machine kan code niet veilig uitvoeren; er is niets uitgevoerd")
            : detail || t("Dat kon niet worden uitgevoerd")
      );
    } finally {
      setRunning(false);
    }
  }

  // CA8: een rit die van een issue komt, start meteen met die opdracht (Franks klik was het begin).
  useEffect(() => {
    if (!beginOpdracht) return;
    setTask(beginOpdracht);
    onBegonnen();
    doTask(beginOpdracht);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doTask(opdracht) {
    const tekst = typeof opdracht === "string" ? opdracht : task;
    if (!tekst.trim()) {
      toast.error(t("Typ eerst wat de agent moet doen."));
      return;
    }
    laatsteRit.current = { soort: "taak", opdracht: tekst };
    setWorking(true);
    setAgentSteps([]);
    setOutcome("");
    setSteps([]);
    // FIX 6: MEEKIJKEN. De lus schrijft elke stap meteen in het log van de
    // werkruimte; dit leest dat log zolang de agent loopt, zodat er per ronde
    // iets in beeld staat in plaats van alleen een draaiend rondje.
    const start = Date.now() / 1000 - 1;
    const meekijken = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/workspaces/${workspace.id}/log`);
        const lopend = stappenSinds(r.data, start);
        if (lopend.length) setAgentSteps(stepsFromAgent(lopend));
      } catch {
        // Meekijken is een extra; het antwoord van de taak blijft de bron.
      }
    }, LOG_MEEKIJKEN_MS);
    try {
      const { data } = await axios.post(`${API}/workspaces/${workspace.id}/task`, { task: tekst });
      naRit(data);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      toast.error(
        status === 402
          ? t("De agent vraagt het Pro-abonnement")
          : status === 403
            ? t("Je routeringsbeleid staat niet toe dat een model dit beantwoordt")
            : typeof detail === "string"
              ? t(detail)
              : t("De agent kon niet starten")
      );
      // Glasronde 0.9.231 punt 1: een rit die niet start omdat er een voorstel openstaat, laat dat voorstel zien zoals
      // het nu is, niet zoals het eerder op het scherm stond.
      if (status === 409) setVoorstelVersie((v) => v + 1);
    } finally {
      clearInterval(meekijken);
      setWorking(false);
    }
  }

  // Na een rit, ook een deelstap (CA7): de stappen, de uitkomst, de bestanden en het voorstel opnieuw.
  function naRit(data) {
    setAgentSteps(stepsFromAgent(data.steps));
    setOutcome(outcomeSentence(data.outcome, data.rounds, data.steps, taal));
    loadFiles();
    setVoorstelVersie((v) => v + 1);
    loadLearned();
    // CA2: een installatie die wacht, staat in de werkruimte; opnieuw lezen zodat de kaart verschijnt.
    axios.get(`${API}/workspaces/${workspace.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }

  async function stopTask() {
    try {
      await axios.post(`${API}/workspaces/${workspace.id}/stop`);
      toast.success(t("De agent stopt na de stap waar hij nu mee bezig is."));
    } catch {
      toast.error(t("Stoppen lukte niet."));
    }
  }

  async function grant(offer) {
    setGranting(true);
    try {
      const { data } = await axios.put(`${API}/workspaces/${workspace.id}/network`, {
        mode: offer.mode,
        hosts: offer.hosts || [],
      });
      setDetail(data);
      // GL222-2: in de taal van de app en niet de Engelse `reads_as` van de backend.
      const hosts = (data.network?.hosts || []).join(", ");
      toast.success(data.network?.mode === "packages" ? t("Netwerk aan voor pakketten (PyPI en npm).")
        : data.network?.mode === "hosts" ? t("Netwerk alleen naar {hosts}.", { hosts }) : t("Het netwerk van deze werkruimte staat uit."));
      if (lastRequest) {
        // Straight back to where they were: the point of granting was the run
        // that failed, so making them press it again is a step for nothing.
        await run();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t("Dat kon niet worden gewijzigd"));
    } finally {
      setGranting(false);
    }
  }

  async function remove(name) {
    await axios.delete(`${API}/workspaces/${workspace.id}/files/${name}`);
    if (name === path) {
      setPath("");
      setContent("");
      setGeladen("");
    }
    loadFiles();
  }

  const level = levelAt(files, folder);

  return (
    <div className={`flex min-h-screen flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      <header className="flex items-center gap-2 border-b px-3 pb-2 pt-[calc(0.5rem+var(--veilig-boven))]">
        <Button variant="ghost" size="icon" className="tap-target" onClick={onBack} aria-label={t("Terug naar de werkruimtes")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">{werkruimteNaam(workspace.name, t)}</h1>
        {/* Glasronde 0.9.234 punt 1 en 2: team en eigen rol naast de naam. */}
        {detail?.repo?.id && <CodeTeamLabel repoId={detail.repo.id} />}
        <NetworkKeuze detail={detail} onGewijzigd={setDetail} />
      </header>

      {/* FIX 5: een kolom met een maximale breedte, zodat op een breed venster
          niets ver naar rechts staat. */}
      <div className="mx-auto w-full max-w-4xl flex-1 space-y-3 p-3" data-testid="code-kolom">
        <CodeVoorbeelden
          onKies={(opdracht) => {
            setTask(opdracht);
            document.getElementById("task-input")?.focus();
          }}
        />
        {/* GL225-4: Issues, Pull requests, Takken en Pull request maken voor de repo van deze werkruimte. */}
        {detail?.repo?.id && (
          <CodeRepoBalk workspaceId={workspace.id} repoId={detail.repo.id} versie={voorstelVersie + vastlegVersie}
                        onLosIssueOp={(n) => { if (!working) doTask(`Los issue #${n} op`); }}
                        onTakWeg={() => {
                          // Glasronde 0.9.230 punt 5: de rit op die tak is afgerond (de backend sluit hem af in het
                          // log); het oude verloop hoort dan niet meer onder Wat de agent deed.
                          setAgentSteps([]);
                          setOutcome("");
                          loadFiles();
                        }} />
        )}
        <section>
          <div className="mb-1.5 flex items-center gap-2">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("Bestanden")}
            </h2>
            <span className="text-xs text-muted-foreground">{files.filter((f) => !f.is_dir).length}</span>
            <Button
              variant="ghost"
              size="sm"
              className="tap-target ml-auto"
              onClick={() => {
                setPath("");
                setContent("");
                setGeladen("");
              }}
              data-testid="new-file"
            >
              <FilePlus className="h-4 w-4 mr-1" />
              {t("Nieuw bestand")}
            </Button>
          </div>
          {folder && (
            <div className="mb-1.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <button type="button" className="tap-target underline" onClick={() => setFolder("")}>
                {t("boven")}
              </button>
              {crumbs(folder).map((step) => (
                <span key={step.path} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  <button type="button" className="tap-target underline" onClick={() => setFolder(step.path)}>
                    {step.name}
                  </button>
                </span>
              ))}
            </div>
          )}
          {level.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("Nog niets. Typ hieronder een bestandsnaam, bijvoorbeeld hallo.py.")}</p>
          )}
          <ul className="space-y-1">
            {level.map((entry) => (
              <li key={entry.path} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => (entry.kind === "folder" ? setFolder(entry.path) : openFile(entry.path))}
                  className="tap-target flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted/60"
                  data-testid={`file-${entry.path}`}
                >
                  {entry.kind === "folder" && <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  {/* min-w-0 and truncate, or one generated path pushes the
                      size and the bin off the row. */}
                  <span className="min-w-0 flex-1 truncate">{entry.name}</span>
                  <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                    {entry.kind === "folder" ? `${entry.count} bestanden` : readableSize(entry.size)}
                  </span>
                </button>
                {entry.kind === "file" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tap-target h-8 w-8 shrink-0"
                    onClick={() => remove(entry.path)}
                    aria-label={`Verwijder ${entry.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <Input
            value={path}
            onChange={(event) => { doorToepassen.current = false; setPath(event.target.value); }}
            placeholder={t("Naam van een nieuw bestand, bijvoorbeeld main.py")}
            className="mb-2"
            aria-label={t("Bestandsnaam")}
            data-testid="file-name"
          />
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            spellCheck={false}
            rows={10}
            placeholder={path ? "" : t("Geen bestand geopend. Kies links een bestand, of typ hierboven een naam voor een nieuw bestand.")}
            aria-label={t("Inhoud van het bestand")}
            className="w-full rounded-lg border bg-muted/30 p-3 font-mono text-sm"
            data-testid="editor"
          />
        </section>

        {learned.length > 0 && (
          <section data-testid="learned-section">
            <div className="mb-1.5 flex items-center gap-2">
              <Brain className="h-3.5 w-3.5 text-muted-foreground" />
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("Wat deze werkruimte de agent leerde")}
              </h2>
              <span className="text-xs text-muted-foreground">{learned.length}</span>
            </div>
            <ul className="space-y-1">
              {learned.map((item) => (
                <li key={item.id} className="flex items-center gap-1">
                  {/* min-w-0, or a long sentence pushes the bin off the row. */}
                  <span className="min-w-0 flex-1 truncate text-sm" title={geleerdeRegel(item.text, t)}
                        data-testid="geleerde-regel">
                    {geleerdeRegel(item.text, t)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tap-target h-8 w-8 shrink-0"
                    onClick={() => forget(item.id, geleerdeRegel(item.text, t))}
                    aria-label={`Vergeet: ${geleerdeRegel(item.text, t)}`}
                    data-testid={`forget-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {agentSteps.length > 0 && (
          <section data-testid="agent-section">
            <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("Wat de agent deed")}
            </h2>
            <AgentSteps steps={agentSteps} outcome={outcome} />
          </section>
        )}

        <RepoVastleggingen workspaceId={workspace.id} versie={voorstelVersie + vastlegVersie} />
        {detail.installatie && (
          <section className="space-y-2 rounded-lg border border-amber-500/40 p-3" data-testid="installatie-kaart">
            <h2 className="text-sm font-semibold">{t("Installatie wacht op jou")}</h2>
            <p className="text-sm">
              {detail.installatie.manager === "npm" ? t("Met npm installeren:") : t("Met pip (PyPI) installeren:")}{" "}
              <code>{(detail.installatie.pakketten || []).join(", ")}</code>
            </p>
            <div className="text-xs text-muted-foreground">{t("Dit komt mee:")}</div>
            <ul className="text-xs" data-testid="installatie-mee">
              {(detail.installatie.mee || []).map((p) => <li key={p.naam}><code>{p.naam}</code> {p.versie}</li>)}
            </ul>
            <div className="flex gap-2">
              <Button size="sm" className="tap-target" disabled={installeren || !installatieToken} onClick={() => installatie("bevestig")}
                      data-testid="installatie-bevestig">{t("Installeer")}</Button>
              <Button size="sm" variant="outline" className="tap-target" disabled={installeren}
                      onClick={() => installatie("weigeren")} data-testid="installatie-weiger">{t("Weiger")}</Button>
            </div>
          </section>
        )}
        <CodeVoorstel
          workspaceId={workspace.id}
          versie={voorstelVersie}
          onGewijzigd={(pad) => {
            loadFiles();
            setVastlegVersie((v) => v + 1);
            herlaadOpen(pad);
          }}
        />

        {steps.length > 0 && (
          <section data-testid="steps-section">
            <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("Wat er gebeurde")}
            </h2>
            <RunSteps steps={steps} onGrant={grant} granting={granting} />
          </section>
        )}
      </div>

      {/* The task bar. Today it runs the open file; this is where a sentence
          goes when the agent loop arrives, which is why it sits apart from the
          editor and at the bottom, where a composer belongs. */}
      {/* Glasronde 0.9.230 (telefoon): dit blok nam bijna het hele scherm in en kon niet kleiner, dus de bestanden en de
          code waren niet te zien. Nu in te klappen (dan alleen de opdrachtregel) en uitgeklapt hooguit 60 procent van
          het scherm, met scrollen binnenin. Ingeklapte delen worden verborgen en niet weggehaald: Deelstappen en de
          serverlog houden zo hun stand. */}
      <div className="sticky bottom-0 border-t bg-background/95 backdrop-blur-sm" data-testid="code-onderblok">
        <div className="mx-auto flex w-full max-w-4xl justify-end px-3 pt-1">
          <button type="button" className="tap-target flex items-center gap-1 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted/60"
                  aria-expanded={!balkIn} aria-controls="code-onderblok-inhoud" data-testid="onderblok-schakelaar"
                  onClick={() => setBalkIn((nu) => { bewaarBalkIn(!nu); return !nu; })}>
            {balkIn ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {balkIn ? t("Uitklappen") : t("Inklappen")}
          </button>
        </div>
        <div id="code-onderblok-inhoud" className="mx-auto max-h-[60dvh] w-full max-w-4xl space-y-2 overflow-y-auto overscroll-contain px-3 pb-3"
             data-testid="onderblok-inhoud">
          {/* FIX 5: TWEE DINGEN, EN ZE HOREN ER VERSCHILLEND UIT TE ZIEN. Boven:
              de agent, die zelf schrijft en uitvoert. Onder: het bestand dat
              nu open staat, zelf uitvoeren. Een echt formulier, zodat Enter (en
              "Start" op een telefoon) de agent ook start. */}
          <form
            className="space-y-1"
            onSubmit={(event) => {
              event.preventDefault();
              if (!working) doTask();
            }}
            data-testid="task-form"
          >
            <div className={balkIn ? "hidden" : "space-y-1"} data-testid="onderblok-instellingen">
            <label htmlFor="task-input" className="text-xs font-medium text-muted-foreground">
              {t("Opdracht voor de agent: hij schrijft de bestanden en voert ze zelf uit")}
            </label>
            {/* LC1: welk model de agent gebruikt; dezelfde keuze als in Instellingen. */}
            <CodeModelKeuze compact />
            {/* CA1 (besluit Frank 31 okt): hooguit 12 rondes per rit, instelbaar per werkruimte. */}
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              {t("Rondes per rit")}
              <select value={detail.max_rondes ?? 12} onChange={(e) => zetRondes(e.target.value)}
                      aria-label={t("Rondes per rit")} className="rounded-md border bg-transparent px-1 py-0.5"
                      data-testid="rondes-kiezer">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            </div>
            <div className="flex gap-2">
              <Input
                id="task-input"
                value={task}
                onChange={(event) => setTask(event.target.value)}
                placeholder={t("Bijvoorbeeld: maak hallo.py dat hallo print en draai het")}
                aria-label={t("Opdracht voor de agent")}
                data-testid="task-input"
              />
              <Button type="submit" className="tap-target shrink-0" disabled={working} data-testid="do-task">
                {working ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bot className="h-4 w-4 mr-1" />}
                {working ? t("Agent werkt…") : t("Start de agent")}
              </Button>
              {/* POST G: de agent stoppen na de stap waar hij nu mee bezig is. */}
              {working && (
                <Button type="button" variant="outline" className="tap-target shrink-0" onClick={stopTask}
                        data-testid="stop-agent">
                  <Square className="h-4 w-4 mr-1" />
                  {t("Stop")}
                </Button>
              )}
            </div>
          </form>
          <div className={balkIn ? "hidden" : "space-y-2"} data-testid="onderblok-rest">
          {/* CA7 (besluit Frank 1 nov): een hele app in deelstappen, 60 minuten, Bevestig per deelstap. */}
          <DeelstappenPaneel workspaceId={workspace.id} verder={deelstapVerder}
                             onRit={(data) => { laatsteRit.current = { soort: "deelstap" }; naRit(data); }} />
          <div className="flex flex-wrap items-center gap-2 border-t pt-2">
            <span className="w-full text-xs text-muted-foreground">
              {t("Of voer het open bestand zelf uit")}{path ? `: ${path}` : ""}
            </span>
            <Button variant="outline" className="tap-target" onClick={save} data-testid="save-file">
              {t("Opslaan")}
            </Button>
            <Button
              variant="outline"
              className="tap-target flex-1"
              onClick={run}
              disabled={running}
              data-testid="run-file"
            >
              {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
              {running ? t("Bezig…") : t("Dit bestand uitvoeren")}
            </Button>
          </div>
          {/* CA3 (besluit Frank 1 nov): het open bestand als server, met poort, log en voorbeeld. */}
          <ServerPaneel workspaceId={workspace.id} path={path}
                        voorStart={() => axios.put(`${API}/workspaces/${workspace.id}/files/${path}`, { content })} />
          </div>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}
